import { DataTable } from "@/components/ui/data-table";
import { User, userColumns } from "@/lib/columns";
import { axiosPrivate } from "@/api/axios";
import React from "react";
import { Row } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, UserPlus, Eye } from "lucide-react";
import CreateUserDialog, { CreateUserPayload } from "./CreateUserDialog";
import UserDetailDialog from "./UserDetailDialog";

const PAGE_LIMIT = 25;

function Users() {
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(1);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ["users", page],
    queryFn: async () => {
      const res = await axiosPrivate.get("/users/", {
        params: { current_page: page, limit: PAGE_LIMIT },
      });
      if (!res.data?.success) throw new Error("Failed to fetch users.");
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

  const users: User[] = (apiResponse?.data || []).filter(
    (u: User) => u.role !== "admin" && u.role !== "expert" && u.role !== "influencer"
  );
  const totalPages: number = apiResponse?.total_pages || 1;

  const createUserMutation = useMutation({
    mutationFn: async (payload: CreateUserPayload) => {
      const res = await axiosPrivate.post("/users/", payload);
      if (!res.data?.success) throw new Error("Failed to create user.");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User created successfully.");
      setCreateOpen(false);
    },
    onError: () => toast.error("Failed to create user. Please try again."),
  });

  const actions = {
    id: "actions",
    header: "Actions",
    cell: ({ row }: { row: Row<User> }) => {
      const confirmActionButtonRef = React.useRef<() => void>(() => {});
      const [openConfirm, setOpenConfirm] = React.useState(false);

      async function makeExpert() {
        try {
          await axiosPrivate.put(`/users/${row.original.id}`, { role: "expert" });
          toast.success(`User #${row.original.id} is now an Expert`);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        } catch {
          toast.error("Something went wrong on the server side");
        }
      }

      async function removeExpert() {
        try {
          await axiosPrivate.put(`/users/${row.original.id}`, { role: "user" });
          toast.success(`User #${row.original.id} role changed back to user`);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        } catch {
          toast.error("Something went wrong on the server side");
        }
      }

      async function makeInfluencer() {
        try {
          await axiosPrivate.put(`/users/${row.original.id}`, { role: "influencer" });
          toast.success(`User #${row.original.id} is now an Influencer`);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        } catch {
          toast.error("Something went wrong on the server side");
        }
      }

      async function removeInfluencer() {
        try {
          await axiosPrivate.put(`/users/${row.original.id}`, { role: "user" });
          toast.success(`User #${row.original.id} role changed back to user`);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        } catch {
          toast.error("Something went wrong on the server side");
        }
      }

      async function handleDelete() {
        try {
          await axiosPrivate.delete(`/users/${row.original.id}`);
          toast.success(`User #${row.original.id} deleted.`);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        } catch {
          toast.error("Something went wrong on the server side");
        }
      }

      return (
        <>
          <ActionsMenu
            extraItems={[
              {
                label: "View Details",
                onClick: () => {
                  setSelectedUser(row.original);
                  setDetailOpen(true);
                },
              },
              ...(row.original.role !== "expert"
                ? [{ label: "Make Expert", onClick: makeExpert }]
                : []),
              ...(row.original.role === "expert"
                ? [{ label: "Remove Expert", onClick: removeExpert }]
                : []),
              ...(row.original.role !== "influencer"
                ? [{ label: "Make Influencer", onClick: makeInfluencer }]
                : []),
              ...(row.original.role === "influencer"
                ? [{ label: "Remove Influencer", onClick: removeInfluencer }]
                : []),
            ]}
            onDeleteConfirm={(proceed) => {
              confirmActionButtonRef.current = proceed;
              setOpenConfirm(true);
            }}
          />
          <ConfirmDialog
            actionButtonRef={confirmActionButtonRef}
            open={openConfirm}
            onOpenChange={setOpenConfirm}
            isLoading={false}
            AlertdialogDescription={`This will permanently delete user #${row.original.id}. Are you sure?`}
          />
        </>
      );
    },
  };

  const columns = React.useMemo(() => [...userColumns, actions], []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl">User Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage all users — control access, assign roles, and view full profiles.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-40 text-muted-foreground">
          Loading users...
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={users}
            placeholder="Filter by username, email, country..."
            filterColumns={["username", "country", "role"]}
            enablePagination={false}
          />
          {/* Pagination */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSave={(payload) => createUserMutation.mutate(payload)}
        isSaving={createUserMutation.isPending}
      />
      <UserDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        user={selectedUser}
      />
    </div>
  );
}

export default Users;

